import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, Power, Search } from "lucide-react";
import { FilterCombobox } from "@/pages/ManageMasters";
import { PaginationBar, usePagination } from "@/components/Pagination";

export function AdminOrgUsersList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [ests, setEsts] = useState([]);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("all");
  const [estFilter, setEstFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => Promise.all([api.get("/admin/org-users"), api.get("/admin/establishments")])
    .then(([u, e]) => { setRows(u.data); setEsts(e.data); })
    .catch(e => toast.error(e.response?.data?.detail || "Load failed"));
  useEffect(() => { load(); }, []);

  const orgNames = useMemo(() => [...new Set(rows.map(r => r.org_name).filter(n => n && n !== "—"))].sort(), [rows]);
  const estNames = useMemo(() => {
    const pool = orgFilter === "all" ? ests : ests.filter(e => e.org_name === orgFilter);
    return [...new Set(pool.map(e => e.name).filter(Boolean))].sort();
  }, [ests, orgFilter]);

  const filtered = useMemo(() => rows.filter(r => {
    if (orgFilter !== "all" && r.org_name !== orgFilter) return false;
    if (estFilter !== "all" && !(r.establishment_names || []).includes(estFilter)) return false;
    if (statusFilter === "active" && !r.is_active) return false;
    if (statusFilter === "inactive" && r.is_active) return false;
    const needle = q.trim().toLowerCase();
    if (needle && !(`${r.name} ${r.email} ${r.phone || ""}`.toLowerCase().includes(needle))) return false;
    return true;
  }), [rows, orgFilter, estFilter, statusFilter, q]);
  const pg = usePagination(filtered, JSON.stringify([q, orgFilter, estFilter, statusFilter]));

  const toggle = async (r) => {
    try {
      const { data } = await api.post(`/admin/org-users/${r.id}/toggle-active`);
      toast.success(data.is_active ? `${r.name} activated` : `${r.name} deactivated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!confirm(`Delete user "${r.name}" (${r.email})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/org-users/${r.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="orgusr-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Organisation User Master</h1>
          <p className="text-muted-foreground">Every organisation user across all orgs and establishments — managed by the SaaS admin.</p>
        </div>
        <Button size="sm" onClick={() => nav("/admin/org-users/new")} data-testid="orgusr-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New User
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-end">
            <div className="relative">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Search</Label>
              <div className="relative mt-1">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
                <Input data-testid="orgusr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or mobile…" className="pl-8 w-56"/>
              </div>
            </div>
            <FilterCombobox label="Organisation" value={orgFilter} options={orgNames} testid="orgusr-filter-org"
              onChange={(v) => { setOrgFilter(v); setEstFilter("all"); }}/>
            <FilterCombobox label="Establishment" value={estFilter} options={estNames} testid="orgusr-filter-est"
              onChange={setEstFilter}/>
            <FilterCombobox label="Status" value={statusFilter} options={["active", "inactive"]} testid="orgusr-filter-status"
              onChange={setStatusFilter}/>
            <Badge variant="outline" className="ml-auto" data-testid="orgusr-count">{filtered.length} of {rows.length} shown</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="orgusr-table">
            <TableHeader>
              <TableRow>
                <TableHead>User Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Organisation Name</TableHead>
                <TableHead>Establishment Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageItems.map(r => (
                <TableRow key={r.id} data-testid={`orgusr-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm">{r.email}</TableCell>
                  <TableCell className="font-mono text-xs">{r.phone || "—"}</TableCell>
                  <TableCell className="text-sm">{r.org_name}</TableCell>
                  <TableCell className="text-sm" data-testid={`orgusr-est-${r.id}`}>
                    {r.primary_establishment}
                    {(r.establishment_names || []).length > 1 && (
                      <Badge variant="outline" className="ml-2 text-[10px]" title={r.establishment_names.join(", ")}
                        data-testid={`orgusr-est-count-${r.id}`}>+{r.establishment_names.length - 1}</Badge>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize text-[10px]">{r.role}</Badge></TableCell>
                  <TableCell>
                    <Badge variant={r.is_active ? "default" : "destructive"} data-testid={`orgusr-status-${r.id}`}>
                      {r.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/admin/org-users/${r.id}/edit`)} data-testid={`orgusr-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5"/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(r)} data-testid={`orgusr-toggle-${r.id}`}
                      title={r.is_active ? "Deactivate" : "Activate"}>
                      <Power className={`w-3.5 h-3.5 ${r.is_active ? "text-destructive" : "text-primary"}`}/>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(r)} data-testid={`orgusr-del-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="orgusr-empty">
                  {rows.length ? "No users match the current filters." : "No organisation users yet"}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <PaginationBar pg={pg}/>
        </CardContent>
      </Card>
    </div>
  );
}

const emptyForm = {
  name: "", email: "", phone: "", password: "", confirm: "",
  org_id: "", branch_ids: [], role: "receptionist", is_active: true,
};

export function AdminOrgUserForm() {
  const { uid } = useParams();
  const isEdit = !!uid;
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [ests, setEsts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/organisation"), api.get("/admin/establishments"), api.get("/roles")])
      .then(([o, e, r]) => {
        setOrgs(o.data);
        setEsts(e.data);
        setRoles(r.data.map(x => x.name));
        if (!isEdit) { setLoading(false); return; }
        api.get("/admin/org-users").then(({ data: users }) => {
          const u = users.find(x => x.id === uid);
          if (!u) { toast.error("User not found"); nav("/admin/org-users"); return; }
          setF({
            name: u.name || "", email: u.email || "", phone: u.phone || "",
            password: "", confirm: "", org_id: u.org_id || "",
            branch_ids: u.branch_ids || [], role: u.role || "receptionist",
            is_active: u.is_active !== false,
          });
          setLoading(false);
        });
      })
      .catch(() => { toast.error("Failed to load"); nav("/admin/org-users"); });
  }, [uid]);

  const orgEsts = useMemo(() => ests.filter(e => e.org_id === f.org_id), [ests, f.org_id]);

  const setOrg = (v) => setF(prev => {
    const valid = new Set(ests.filter(e => e.org_id === v).map(e => e.id));
    return { ...prev, org_id: v, branch_ids: prev.branch_ids.filter(b => valid.has(b)) };
  });
  const toggleBranch = (bid, on) => setF(prev => ({
    ...prev,
    branch_ids: on ? [...prev.branch_ids, bid] : prev.branch_ids.filter(b => b !== bid),
  }));

  const save = async (e) => {
    e?.preventDefault();
    if (!f.name.trim()) { toast.error("Full Name is required"); return; }
    if (!f.email.trim()) { toast.error("Email is required"); return; }
    if (!f.org_id) { toast.error("Select an organisation"); return; }
    if (!isEdit && !f.password) { toast.error("Password is required"); return; }
    if (f.password && f.password !== f.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const payload = { name: f.name, email: f.email, phone: f.phone, org_id: f.org_id,
                        branch_ids: f.branch_ids, role: f.role, is_active: f.is_active,
                        password: f.password || null };
      if (isEdit) await api.patch(`/admin/org-users/${uid}`, payload);
      else await api.post("/admin/org-users", payload);
      toast.success(isEdit ? "User updated" : "User created");
      nav("/admin/org-users");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="orgusr-form-page">
      <Link to="/admin/org-users" data-testid="orgusr-back"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="w-3 h-3"/> Back to Organisation User Master
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEdit ? "Edit User" : "New User"}</CardTitle>
          <CardDescription>Changes reflect immediately on the organisation's User page under Org &amp; Access.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5" data-testid="orgusr-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input data-testid="orgusr-name" value={f.name} onChange={(e) => setF(p => ({ ...p, name: e.target.value }))} className="mt-1"/>
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="orgusr-email" type="email" value={f.email} onChange={(e) => setF(p => ({ ...p, email: e.target.value }))} className="mt-1"/>
              </div>
              <div>
                <Label>Mobile</Label>
                <Input data-testid="orgusr-mobile" value={f.phone} onChange={(e) => setF(p => ({ ...p, phone: e.target.value }))} className="mt-1 font-mono"/>
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={f.role} onValueChange={(v) => setF(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="orgusr-role" className="mt-1"><SelectValue placeholder="Select role"/></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => <SelectItem key={r} data-testid={`orgusr-role-opt-${r}`} value={r} className="capitalize">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Password {isEdit ? "(leave blank to keep current)" : "*"}</Label>
                <Input data-testid="orgusr-password" type="password" value={f.password} onChange={(e) => setF(p => ({ ...p, password: e.target.value }))} className="mt-1" autoComplete="new-password"/>
              </div>
              <div>
                <Label>Confirm Password {isEdit ? "" : "*"}</Label>
                <Input data-testid="orgusr-confirm" type="password" value={f.confirm} onChange={(e) => setF(p => ({ ...p, confirm: e.target.value }))} className="mt-1" autoComplete="new-password"/>
              </div>
              <div>
                <Label>Organisation *</Label>
                <Select value={f.org_id} onValueChange={setOrg}>
                  <SelectTrigger data-testid="orgusr-org" className="mt-1"><SelectValue placeholder="Select organisation"/></SelectTrigger>
                  <SelectContent>
                    {orgs.map(o => <SelectItem key={o.id} data-testid={`orgusr-org-opt-${o.id}`} value={o.id}>{o.business_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end justify-between border border-border rounded-sm px-3 py-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Active</Label>
                <Switch data-testid="orgusr-status" checked={f.is_active} onCheckedChange={(v) => setF(p => ({ ...p, is_active: v }))}/>
              </div>
            </div>

            <div>
              <Label>Establishments (one or more — first selected is primary)</Label>
              <div className="mt-1 border border-border rounded-sm p-3 space-y-2" data-testid="orgusr-branches">
                {!f.org_id && <div className="text-xs text-muted-foreground" data-testid="orgusr-branches-hint">Select an organisation first</div>}
                {f.org_id && !orgEsts.length && <div className="text-xs text-muted-foreground" data-testid="orgusr-branches-none">No establishments in this organisation</div>}
                {orgEsts.map(b => (
                  <label key={b.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`orgusr-branch-row-${b.id}`}>
                    <Checkbox data-testid={`orgusr-branch-${b.id}`} checked={f.branch_ids.includes(b.id)}
                      onCheckedChange={(v) => toggleBranch(b.id, !!v)}/>
                    {b.name}
                    {f.branch_ids[0] === b.id && <Badge variant="outline" className="text-[10px]">Primary</Badge>}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button data-testid="orgusr-save" type="submit" disabled={busy}>
                {busy ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
              </Button>
              <Button data-testid="orgusr-cancel" type="button" variant="outline" onClick={() => nav("/admin/org-users")}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
